import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const THEME = {
    base: '#1c1917', // Stone 900
    accent: '#fbbf24', // Gold
    skin: '#ffdbac',   // Warm skin tone
    hair: '#e2e8f0',   // Silvery Slate hair
    eyeColor: '#1e293b' // Dark eyes
};

interface PixelAvatarProps {
    isTalking?: boolean;
    isThinking?: boolean;
    isNodding?: boolean;
    isSurprised?: boolean;
    section?: 'gallery' | 'written' | 'artist' | 'default';
}

const PixelAvatar: React.FC<PixelAvatarProps> = ({
    isTalking = false,
    isThinking = false,
    isNodding = false,
    isSurprised = false,
    section = 'default'
}) => {
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [isBlinking, setIsBlinking] = useState(false);
    const avatarRef = useRef<HTMLDivElement>(null);

    // Mouse Tracking - Only on non-touch devices
    useEffect(() => {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (isTouchDevice) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (!avatarRef.current) return;
            const rect = avatarRef.current.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            const dx = (e.clientX - centerX) / (window.innerWidth / 2);
            const dy = (e.clientY - centerY) / (window.innerHeight / 2);

            setMousePos({
                x: Math.max(-1, Math.min(1, dx)) * 1.5,
                y: Math.max(-1, Math.min(1, dy)) * 1.5
            });
        };

        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, []);

    // Blinking logic
    useEffect(() => {
        const blinkInterval = setInterval(() => {
            if (Math.random() > 0.8) {
                setIsBlinking(true);
                setTimeout(() => setIsBlinking(false), 150);
            }
        }, 3000);
        return () => clearInterval(blinkInterval);
    }, []);

    return (
        <div ref={avatarRef} className="relative w-full h-full p-0.5">
            <div className="relative z-10 w-full h-full rounded-full overflow-hidden shadow-2xl border border-[#fbbf24]/30 bg-[#1c1917]">
                <svg viewBox="0 0 32 32" className="w-full h-full" shapeRendering="crispEdges">
                    {/* HAIR */}
                    <rect x="6" y="4" width="20" height="8" fill={THEME.hair} opacity="0.4" />
                    <rect x="4" y="8" width="5" height="12" fill={THEME.hair} />
                    <rect x="3" y="10" width="2" height="8" fill={THEME.hair} opacity="0.8" />
                    <rect x="23" y="8" width="5" height="12" fill={THEME.hair} />
                    <rect x="27" y="10" width="2" height="8" fill={THEME.hair} opacity="0.8" />

                    {/* FACE */}
                    <rect x="8" y="6" width="16" height="20" fill={THEME.skin} />
                    <rect x="10" y="26" width="12" height="2" fill={THEME.skin} />
                    <rect x="10" y="5" width="12" height="3" fill={THEME.skin} />

                    {/* GLASSES */}
                    <rect x="7" y="14" width="18" height="1" fill="#475569" />
                    <rect x="9" y="14" width="6" height="5" fill="white" opacity="0.2" />
                    <rect x="17" y="14" width="6" height="5" fill="white" opacity="0.2" />
                    <rect x="9" y="19" width="6" height="1" fill="#475569" opacity="0.5" />
                    <rect x="17" y="19" width="6" height="1" fill="#475569" opacity="0.5" />

                    {/* EYES with tracking and blinking */}
                    {!isBlinking ? (
                        <>
                            {/* Pupils move relative to base x:11,19 y:16 */}
                            <rect
                                x={11 + mousePos.x}
                                y={16 + mousePos.y}
                                width="2" height="2"
                                fill={THEME.eyeColor}
                            />
                            <rect
                                x={19 + mousePos.x}
                                y={16 + mousePos.y}
                                width="2" height="2"
                                fill={THEME.eyeColor}
                            />
                        </>
                    ) : (
                        <>
                            <rect x="11" y="17" width="2" height="1" fill="#475569" opacity="0.5" />
                            <rect x="19" y="17" width="2" height="1" fill="#475569" opacity="0.5" />
                        </>
                    )}

                    {/* NOSE */}
                    <rect x="15" y="18" width="2" height="3" fill="#000" opacity="0.05" />

                    {/* MOUTH - Expressive Gestures */}
                    <motion.g
                        animate={isSurprised ? {
                            scale: 1.5,
                            y: 1
                        } : isTalking ? {
                            y: [0, 0.5, 0],
                            scaleY: [1, 2.5, 1]
                        } : isNodding ? {
                            y: [0, 0.5, 0]
                        } : {
                            scaleY: 1,
                            y: 0,
                            scale: 1
                        }}
                        transition={isTalking || isNodding ? {
                            duration: 0.25,
                            repeat: isTalking ? Infinity : 2,
                            ease: "easeInOut"
                        } : { duration: 0.2 }}
                    >
                        {/* Base Mouth - More vibrant pink/red */}
                        <rect
                            x={isSurprised ? "14.5" : "13.5"} y="23"
                            width={isSurprised ? "3" : "5"} height={isSurprised ? "2" : "1"}
                            fill="#f43f5e" opacity="0.6"
                        />
                        {/* Talking/Surprise Detail */}
                        {(isTalking || isSurprised) && (
                            <rect
                                x={isSurprised ? "15" : "14.25"} y={isSurprised ? "23.5" : "23.2"}
                                width={isSurprised ? "2" : "3.5"} height={isSurprised ? "1" : "1.5"}
                                fill="#e11d48" opacity="0.8"
                            />
                        )}
                    </motion.g>

                    {/* THINKING - Sparkles or Bubbles */}
                    {isThinking && (
                        <motion.g
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <circle cx="26" cy="6" r="1" fill={THEME.accent} className="animate-pulse" />
                            <circle cx="28" cy="4" r="0.5" fill={THEME.accent} opacity="0.6" />
                        </motion.g>
                    )}

                    {/* Section characteristic details - Moved away from mouth area */}
                    {section === 'gallery' && (
                        <motion.g
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transform="translate(26, 26)"
                        >
                            <rect x="0" y="0" width="1" height="4" fill="#8B4513" transform="rotate(-45)" />
                            <rect x="2" y="-2" width="2" height="2" fill="#fbbf24" />
                        </motion.g>
                    )}
                    {section === 'written' && (
                        <motion.g
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transform="translate(4, 26)"
                        >
                            <rect x="0" y="0" width="1" height="5" fill="#1e293b" transform="rotate(20)" />
                        </motion.g>
                    )}
                    {section === 'artist' && (
                        <circle cx="26" cy="26" r="2" fill={THEME.accent} opacity="0.3">
                            <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
                        </circle>
                    )}
                </svg>
            </div>

            {/* Subtle Aura */}
            <motion.div
                className="absolute inset-0 rounded-full border border-[#fbbf24]/50 z-0"
                animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.1, 0.2] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />
        </div>
    );
};

export default PixelAvatar;
